import Cookies from 'js-cookie';
import { ErrorInfo, PropsWithChildren, useEffect } from 'react';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';

import { ErrorFallback } from '@/components/organisms/ErrorFallback';
import { env } from '@/env';

const ErrorBoundaryProvider = (props: PropsWithChildren) => (
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
    onError={(er, info) => {
      sendErrorLogToServer(er, info);
    }}
  >
    <AsyncErrorHandlerProvider>{props.children}</AsyncErrorHandlerProvider>
  </ErrorBoundary>
);

const AsyncErrorHandlerProvider = (props: PropsWithChildren) => {
  const { showBoundary } = useErrorBoundary();

  // 非同期のエラーはここでキャッチする
  useEffect(() => {
    const handler = async (ev: PromiseRejectionEvent) => {
      showBoundary(ev);
      const error =
        ev.reason instanceof Error ? ev.reason : new Error(String(ev.reason));
      await sendErrorLogToServer(error);
    };
    window.addEventListener('unhandledrejection', handler);

    return () => window.removeEventListener('unhandledrejection', handler);
  }, [showBoundary]);

  return <>{props.children}</>;
};

export default ErrorBoundaryProvider;

// console.errorをオーバーライド
const originalConsoleError = console.error;

console.error = (...args: any[]) => {
  // 引数を結合してエラーメッセージを生成
  const errorMessage = args
    .map((arg) =>
      arg instanceof Error ? arg.message : String(JSON.stringify(arg))
    )
    .join(' ');
  const errorStack = args.find((arg) => arg instanceof Error)?.stack;

  // サーバーにエラーログを送信
  sendErrorLogToServer(new Error(errorMessage), { componentStack: errorStack });

  // 元のconsole.errorを呼び出してログをコンソールに出力
  originalConsoleError.apply(console, args);
};

// エラーログのサーバーへの送信メソッド
const sendErrorLogToServer = async (error: Error, info?: ErrorInfo) => {
  const { message, stack } = error;
  await fetch(env.REACT_APP_CLIENT_LOG_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message,
      stack,
      componentStack: info?.componentStack || '',
      level: 'error',
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      screen: {
        width: window.screen.width,
        height: window.screen.height,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      language: navigator.language,
      chatUserId: Cookies.get('RV_USER_ID') ?? '',
    }),
  });
};
